/**
 * AR Demo — seed for the blanket cash-receipt ingestion matcher.
 *
 * THROWAWAY DEMO CODE. Builds the `cashReceiptIngestion` slice that backs the
 * three-column matcher: linked bank accounts, the day's bank transactions (left
 * column) and the open-invoice ledger awaiting payment (middle column — AR
 * invoices + AP invoices). Remittances no longer render as their own rows; they
 * link a receipt to the invoices it pays (riding the connector arrow) and feed
 * both the right-column breakdown and the real Remittances screen.
 *
 * The transactions are a realistic mix: customer receipts (which reconcile to
 * the invoices on one or more remittances — one is a combined wire Gently
 * auto-splits across two customers), two short-pays, plus non-receipt traffic
 * (bank fees, outbound AP payments) Gently auto-categorises. Reveal order is
 * deliberately NOT aligned with the invoice list order, so matches land out of
 * sequence and the invoice rows animate into place as they cross off. One
 * remittance has no deposit yet (payment hasn't landed) so its invoice stays
 * uncrossed, and several invoices have no remittance at all — still outstanding
 * with no information.
 *
 * Outcomes are baked in here; the on-page run only animates the reveal. Reset
 * re-seeds to the cold-open `idle` state. Self-contained own ids/brand/date.
 * Delete with the rest of `src/demo/ar`.
 */

import type { RemittanceDetail, RemittanceListItem } from '@erp/rpc';
import type {
  DemoApInvoice,
  DemoArInvoice,
  DemoCashReceiptDeposit,
  DemoCashReceiptIngestionState,
  DemoLinkedBankAccount,
  DemoRemittanceEntry,
} from './fixture-store.types';

/** Stable demo ids, namespaced `cr-` to avoid clashes with other seeds. */
const CR_ID = {
  acctOperating: 'cr-acct-operating',
  acctLockbox: 'cr-acct-lockbox',
  acctWire: 'cr-acct-wire',
} as const;

function dateOnlyDaysFromNow(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

const postedDate = dateOnlyDaysFromNow(-1);
const receivedDate = dateOnlyDaysFromNow(-2);

// ---------------------------------------------------------------------------
// Linked bank accounts the run sweeps.
// ---------------------------------------------------------------------------
const accounts: DemoLinkedBankAccount[] = [
  {
    id: CR_ID.acctOperating,
    name: 'Operating Account',
    institution: 'JPMorgan Chase',
    mask: '4821',
    kind: 'operating',
  },
  {
    id: CR_ID.acctLockbox,
    name: 'Lockbox Account',
    institution: 'Bank of America',
    mask: '7290',
    kind: 'lockbox',
  },
  {
    id: CR_ID.acctWire,
    name: 'Wire / FX Account',
    institution: 'Wells Fargo',
    mask: '1153',
    kind: 'wire',
  },
];

// ---------------------------------------------------------------------------
// Remittance advices (middle column). Each is awaiting its bank match at cold
// open; the matching deposit crosses it off as it's swept in. RA-7350 has no
// deposit yet (payment hasn't landed) and never crosses off. Fairview + Oakline
// are both cleared by one combined wire (cr-dep-combined).
// ---------------------------------------------------------------------------
const remittances: DemoRemittanceEntry[] = [
  {
    id: 'cr-rem-crestline',
    reference: 'RA-7741',
    customerName: 'Crestline Markets',
    receivedDate,
    totalPaid: '70150.00',
    matchedDepositId: 'cr-dep-crestline',
    lineItems: [
      {
        invoiceRef: 'INV-48810',
        billedAmount: '25000.00',
        paidAmount: '23750.00',
      },
      {
        invoiceRef: 'INV-48811',
        billedAmount: '18500.00',
        paidAmount: '18500.00',
      },
      {
        invoiceRef: 'INV-48812',
        billedAmount: '31000.00',
        paidAmount: '27900.00',
      },
    ],
  },
  {
    id: 'cr-rem-ridgeway',
    reference: 'RA-5521',
    customerName: 'Ridgeway Foods',
    receivedDate,
    totalPaid: '42980.00',
    matchedDepositId: 'cr-dep-ridgeway',
    lineItems: [
      {
        invoiceRef: 'INV-51002',
        billedAmount: '21490.00',
        paidAmount: '21490.00',
      },
      {
        invoiceRef: 'INV-51003',
        billedAmount: '21490.00',
        paidAmount: '21490.00',
      },
    ],
  },
  {
    id: 'cr-rem-northpoint',
    reference: 'RA-4410',
    customerName: 'Northpoint Grocers',
    receivedDate,
    totalPaid: '8640.00',
    matchedDepositId: 'cr-dep-northpoint',
    lineItems: [
      {
        invoiceRef: 'INV-50890',
        billedAmount: '8640.00',
        paidAmount: '8640.00',
      },
    ],
  },
  {
    id: 'cr-rem-harbor',
    reference: 'RA-6018',
    customerName: 'Harbor Distributing',
    receivedDate,
    totalPaid: '15300.00',
    matchedDepositId: 'cr-dep-harbor',
    lineItems: [
      {
        invoiceRef: 'INV-49770',
        billedAmount: '9000.00',
        paidAmount: '9000.00',
      },
      {
        invoiceRef: 'INV-49771',
        billedAmount: '6300.00',
        paidAmount: '6300.00',
      },
    ],
  },
  {
    id: 'cr-rem-summit',
    reference: 'RA-6044',
    customerName: 'Summit Wholesale',
    receivedDate,
    totalPaid: '21450.00',
    matchedDepositId: 'cr-dep-summit',
    lineItems: [
      {
        invoiceRef: 'INV-50120',
        billedAmount: '22350.00',
        paidAmount: '21450.00',
      },
    ],
  },
  {
    id: 'cr-rem-valeport',
    reference: 'RA-7102',
    customerName: 'Valeport International',
    receivedDate,
    totalPaid: '63200.00',
    matchedDepositId: 'cr-dep-valeport',
    lineItems: [
      {
        invoiceRef: 'INV-50455',
        billedAmount: '20000.00',
        paidAmount: '20000.00',
      },
      {
        invoiceRef: 'INV-50456',
        billedAmount: '25200.00',
        paidAmount: '25200.00',
      },
      {
        invoiceRef: 'INV-50457',
        billedAmount: '18000.00',
        paidAmount: '18000.00',
      },
    ],
  },
  {
    // Cleared by the combined wire (cr-dep-combined), alongside Oakline.
    id: 'cr-rem-fairview',
    reference: 'RA-7720',
    customerName: 'Fairview Foods',
    receivedDate,
    totalPaid: '13275.00',
    matchedDepositId: 'cr-dep-combined',
    lineItems: [
      {
        invoiceRef: 'INV-50710',
        billedAmount: '13275.00',
        paidAmount: '13275.00',
      },
    ],
  },
  {
    // Cleared by the combined wire (cr-dep-combined), alongside Fairview.
    id: 'cr-rem-oakline',
    reference: 'RA-7731',
    customerName: 'Oakline Distributors',
    receivedDate,
    totalPaid: '18600.00',
    matchedDepositId: 'cr-dep-combined',
    lineItems: [
      {
        invoiceRef: 'INV-50720',
        billedAmount: '9300.00',
        paidAmount: '9300.00',
      },
      {
        invoiceRef: 'INV-50721',
        billedAmount: '9300.00',
        paidAmount: '9300.00',
      },
    ],
  },
  {
    // No deposit has landed for this advice — stays in the middle, uncrossed.
    id: 'cr-rem-brookfield',
    reference: 'RA-7350',
    customerName: 'Brookfield Retail',
    receivedDate,
    totalPaid: '12750.00',
    matchedDepositId: null,
    lineItems: [
      {
        invoiceRef: 'INV-50990',
        billedAmount: '12750.00',
        paidAmount: '12750.00',
      },
    ],
  },
];

// ---------------------------------------------------------------------------
// Bank transactions (left column), in reveal order. Customer receipts are
// interleaved with bank fees + outbound AP payments; the combined wire lands
// fourth and auto-splits across Fairview + Oakline. Order is intentionally not
// aligned with the invoice list, so invoices cross off out of sequence.
// ---------------------------------------------------------------------------
const deposits: DemoCashReceiptDeposit[] = [
  {
    id: 'cr-dep-ridgeway',
    accountId: CR_ID.acctOperating,
    kind: 'customer_receipt',
    direction: 'credit',
    postedDate,
    amount: '42980.00',
    description: 'ACH CREDIT RIDGEWAY FOODS INV 51002 51003',
    method: 'ach',
    customerName: 'Ridgeway Foods',
    matchedDocumentIds: ['cr-rem-ridgeway'],
    matchConfidence: 99,
    outcome: 'matched',
    issueDetail: null,
    shortPayAmount: null,
    splitNote: null,
  },
  {
    id: 'cr-dep-wire-fee',
    accountId: CR_ID.acctWire,
    kind: 'bank_fee',
    direction: 'debit',
    postedDate,
    amount: '45.00',
    description: 'WIRE TRANSFER FEE',
    method: 'wire',
    customerName: null,
    matchedDocumentIds: [],
    matchConfidence: null,
    outcome: 'matched',
    issueDetail: null,
    shortPayAmount: null,
    splitNote: 'Categorised as a bank fee — no cash receipt needed.',
  },
  {
    id: 'cr-dep-summit',
    accountId: CR_ID.acctLockbox,
    kind: 'customer_receipt',
    direction: 'credit',
    postedDate,
    amount: '21450.00',
    description: 'LOCKBOX CHECK 22117 SUMMIT WHOLESALE',
    method: 'check',
    customerName: 'Summit Wholesale',
    matchedDocumentIds: ['cr-rem-summit'],
    matchConfidence: 82,
    outcome: 'short_pay',
    issueDetail:
      'Check is $900 under the invoice total — likely an unauthorized early-pay discount taken outside terms.',
    shortPayAmount: '900.00',
    splitNote: null,
  },
  {
    id: 'cr-dep-combined',
    accountId: CR_ID.acctWire,
    kind: 'customer_receipt',
    direction: 'credit',
    postedDate,
    amount: '31875.00',
    description: 'WIRE IN COMBINED PAYMENT — FAIRVIEW / OAKLINE',
    method: 'wire',
    customerName: 'Fairview Foods + Oakline Distributors',
    matchedDocumentIds: ['cr-rem-fairview', 'cr-rem-oakline'],
    matchConfidence: 94,
    outcome: 'matched',
    issueDetail: null,
    shortPayAmount: null,
    splitNote:
      'Combined wire auto-split across two remittances by reference and amount — RA-7720 (Fairview) + RA-7731 (Oakline).',
  },
  {
    id: 'cr-dep-ap-acme',
    accountId: CR_ID.acctOperating,
    kind: 'ap_payment',
    direction: 'debit',
    postedDate,
    amount: '18400.00',
    description: 'ACH DEBIT — SUPPLIER PAYMENT ACME PACKAGING',
    method: 'ach',
    customerName: 'Acme Packaging',
    matchedDocumentIds: ['cr-ap-acme'],
    matchConfidence: 98,
    outcome: 'matched',
    issueDetail: null,
    shortPayAmount: null,
    splitNote: null,
  },
  {
    id: 'cr-dep-crestline',
    accountId: CR_ID.acctOperating,
    kind: 'customer_receipt',
    direction: 'credit',
    postedDate,
    amount: '70150.00',
    description: 'ACH CREDIT CRESTLINE MARKETS RA-7741',
    method: 'ach',
    customerName: 'Crestline Markets',
    matchedDocumentIds: ['cr-rem-crestline'],
    matchConfidence: 98,
    outcome: 'short_pay',
    issueDetail:
      'Paid $1,250 short on INV-48810 with no reason cited; $3,100 short on INV-48812 citing chargeback #CR-DED-99214.',
    shortPayAmount: '4350.00',
    splitNote: null,
  },
  {
    id: 'cr-dep-northpoint',
    accountId: CR_ID.acctOperating,
    kind: 'customer_receipt',
    direction: 'credit',
    postedDate,
    amount: '8640.00',
    description: 'ACH CREDIT NORTHPOINT GROCERS INV-50890',
    method: 'ach',
    customerName: 'Northpoint Grocers',
    matchedDocumentIds: ['cr-rem-northpoint'],
    matchConfidence: 97,
    outcome: 'matched',
    issueDetail: null,
    shortPayAmount: null,
    splitNote: null,
  },
  {
    id: 'cr-dep-acct-fee',
    accountId: CR_ID.acctOperating,
    kind: 'bank_fee',
    direction: 'debit',
    postedDate,
    amount: '30.00',
    description: 'MONTHLY ACCOUNT MAINTENANCE FEE',
    method: 'ach',
    customerName: null,
    matchedDocumentIds: [],
    matchConfidence: null,
    outcome: 'matched',
    issueDetail: null,
    shortPayAmount: null,
    splitNote: 'Categorised as a bank fee — no cash receipt needed.',
  },
  {
    id: 'cr-dep-valeport',
    accountId: CR_ID.acctWire,
    kind: 'customer_receipt',
    direction: 'credit',
    postedDate,
    amount: '63200.00',
    description: 'WIRE IN VALEPORT INTERNATIONAL REF 88231',
    method: 'wire',
    customerName: 'Valeport International',
    matchedDocumentIds: ['cr-rem-valeport'],
    matchConfidence: 96,
    outcome: 'matched',
    issueDetail: null,
    shortPayAmount: null,
    splitNote: null,
  },
  {
    id: 'cr-dep-harbor',
    accountId: CR_ID.acctLockbox,
    kind: 'customer_receipt',
    direction: 'credit',
    postedDate,
    amount: '15300.00',
    description: 'LOCKBOX CHECK 10482 HARBOR DISTRIBUTING',
    method: 'check',
    customerName: 'Harbor Distributing',
    matchedDocumentIds: ['cr-rem-harbor'],
    matchConfidence: 95,
    outcome: 'matched',
    issueDetail: null,
    shortPayAmount: null,
    splitNote: null,
  },
  {
    id: 'cr-dep-ap-payroll',
    accountId: CR_ID.acctOperating,
    kind: 'ap_payment',
    direction: 'debit',
    postedDate,
    amount: '9820.00',
    description: 'ACH DEBIT — SUPPLIER PAYMENT MERIDIAN LOGISTICS',
    method: 'ach',
    customerName: 'Meridian Logistics',
    matchedDocumentIds: ['cr-ap-meridian'],
    matchConfidence: 97,
    outcome: 'matched',
    issueDetail: null,
    shortPayAmount: null,
    splitNote: null,
  },
];

// ---------------------------------------------------------------------------
// AP invoices (middle column, outgoing section). Each is awaiting its payment
// at cold open; the matching AP payment crosses it off as it's swept in.
// BILL-7790 has no payment yet (unpaid) and stays uncrossed.
// ---------------------------------------------------------------------------
const apInvoices: DemoApInvoice[] = [
  {
    id: 'cr-ap-acme',
    reference: 'BILL-7781',
    supplierName: 'Acme Packaging',
    invoiceDate: receivedDate,
    totalDue: '18400.00',
    matchedDepositId: 'cr-dep-ap-acme',
    lineItems: [
      {
        invoiceRef: 'PO-5521',
        billedAmount: '12400.00',
        paidAmount: '12400.00',
      },
      { invoiceRef: 'PO-5530', billedAmount: '6000.00', paidAmount: '6000.00' },
    ],
  },
  {
    id: 'cr-ap-meridian',
    reference: 'BILL-7782',
    supplierName: 'Meridian Logistics',
    invoiceDate: receivedDate,
    totalDue: '9820.00',
    matchedDepositId: 'cr-dep-ap-payroll',
    lineItems: [
      { invoiceRef: 'PO-5604', billedAmount: '9820.00', paidAmount: '9820.00' },
    ],
  },
  {
    // No payment has gone out for this bill yet — stays uncrossed.
    id: 'cr-ap-crown',
    reference: 'BILL-7790',
    supplierName: 'Crown Supply Co.',
    invoiceDate: receivedDate,
    totalDue: '6450.00',
    matchedDepositId: null,
    lineItems: [
      { invoiceRef: 'PO-5618', billedAmount: '6450.00', paidAmount: '6450.00' },
    ],
  },
];

// ---------------------------------------------------------------------------
// AR open-invoice ledger (middle column, incoming section). Every outstanding
// invoice lists here. Covered invoices are derived straight from the remittance
// line items (one source of truth, billed/paid stay in lockstep) and cross off
// when their remittance's deposit is swept in. INV-50990 (Brookfield) has a
// remittance but no deposit, so it stays open as "awaiting deposit".
//
// The OUTSTANDING_NO_REMITTANCE invoices below have no remittance at all — no
// payment, no advice, no info on when they'll pay. They never cross off; they're
// the reason the middle shows invoices rather than remittances: you can see what
// is still open and uninformed, not just what already has a remittance.
// ---------------------------------------------------------------------------
function arInvoicesFromRemittances(): DemoArInvoice[] {
  const rows: DemoArInvoice[] = [];
  for (const r of remittances) {
    for (const line of r.lineItems) {
      rows.push({
        id: `cr-inv-${line.invoiceRef}`,
        reference: line.invoiceRef,
        customerName: r.customerName,
        invoiceDate: r.receivedDate,
        billedAmount: line.billedAmount,
        remittanceId: r.id,
        paidAmount: line.paidAmount,
      });
    }
  }
  return rows;
}

const OUTSTANDING_NO_REMITTANCE: DemoArInvoice[] = [
  {
    id: 'cr-inv-out-crestline',
    reference: 'INV-50180',
    customerName: 'Crestline Markets',
    invoiceDate: dateOnlyDaysFromNow(-42),
    billedAmount: '16400.00',
    remittanceId: null,
    paidAmount: null,
  },
  {
    id: 'cr-inv-out-harbor',
    reference: 'INV-49905',
    customerName: 'Harbor Distributing',
    invoiceDate: dateOnlyDaysFromNow(-28),
    billedAmount: '7850.00',
    remittanceId: null,
    paidAmount: null,
  },
  {
    id: 'cr-inv-out-summit',
    reference: 'INV-50640',
    customerName: 'Summit Wholesale',
    invoiceDate: dateOnlyDaysFromNow(-17),
    billedAmount: '12990.00',
    remittanceId: null,
    paidAmount: null,
  },
  {
    id: 'cr-inv-out-valeport',
    reference: 'INV-51210',
    customerName: 'Valeport International',
    invoiceDate: dateOnlyDaysFromNow(-9),
    billedAmount: '28100.00',
    remittanceId: null,
    paidAmount: null,
  },
];

const arInvoices: DemoArInvoice[] = [
  ...arInvoicesFromRemittances(),
  ...OUTSTANDING_NO_REMITTANCE,
];

// ---------------------------------------------------------------------------
// Bridge to the real Remittances screen. The matcher's middle-column advices
// must also exist in `remittancesById` / `remittanceList` (the slices the
// `useArRemittance(s)` shims serve) so they show up on the Remittances page.
// We map each `DemoRemittanceEntry` to the real RPC shapes; ids aren't UUID-
// validated on the demo (shim) path, so the `cr-rem-*` ids are fine.
// ---------------------------------------------------------------------------
function toRemittanceDetail(r: DemoRemittanceEntry): RemittanceDetail {
  return {
    id: r.id,
    channel: 'email',
    receivedAt: new Date(r.receivedDate).toISOString(),
    statedCustomerName: r.customerName,
    statedTotalAmount: r.totalPaid,
    statedPaymentDate: r.receivedDate,
    statedPaymentMethod: null,
    statedReferenceNo: r.reference,
    senderEmail: null,
    linkedPaymentId: null,
    linkedPaymentRef: null,
    matchedCustomer: { id: `${r.id}-cust`, name: r.customerName },
    bankCleared: false,
    lineItems: r.lineItems.map((line, i) => ({
      id: `${r.id}-li-${i}`,
      remittanceAdviceId: r.id,
      rawInvoiceRef: line.invoiceRef,
      invoiceId: `${r.id}-inv-${i}`,
      statedAmount: line.paidAmount,
      kind: 'invoice_payment',
      matchStatus: 'unmatched',
      matchReason: null,
      invoice: {
        id: `${r.id}-inv-${i}`,
        invoiceNumber: line.invoiceRef,
        invoiceDate: r.receivedDate,
        netAmount: line.billedAmount,
        balanceDue: line.billedAmount,
        customerName: r.customerName,
        status: 'open',
      },
    })),
    sourceEmail: null,
  };
}

function toRemittanceListItem(detail: RemittanceDetail): RemittanceListItem {
  return {
    id: detail.id,
    channel: detail.channel,
    receivedAt: detail.receivedAt,
    statedCustomerName: detail.statedCustomerName,
    statedTotalAmount: detail.statedTotalAmount,
    statedReferenceNo: detail.statedReferenceNo,
    senderEmail: detail.senderEmail,
    linkedPaymentId: detail.linkedPaymentId,
    linkedPaymentRef: detail.linkedPaymentRef,
    matchedCustomer: detail.matchedCustomer,
    lineItemSummary: {
      total: detail.lineItems.length,
      matched: 0,
      autoMatched: 0,
      unmatched: detail.lineItems.length,
      ambiguous: 0,
      rejected: 0,
    },
  };
}

/**
 * The matcher's remittance advices mapped to real RPC records, for merging into
 * `remittancesById` / `remittanceList` so they appear on the Remittances screen.
 * Caller de-dupes by `statedReferenceNo` against any already-seeded advices.
 */
export function buildCashReceiptRemittanceRecords(): {
  byId: Record<string, RemittanceDetail>;
  list: RemittanceListItem[];
} {
  const details = remittances.map(toRemittanceDetail);
  const byId: Record<string, RemittanceDetail> = {};
  for (const d of details) byId[d.id] = d;
  return { byId, list: details.map(toRemittanceListItem) };
}

/**
 * Cold-open seed for the blanket cash-receipt ingestion matcher: accounts +
 * remittances + transactions loaded, nothing swept yet.
 */
export function buildCashReceiptIngestionSeed(): DemoCashReceiptIngestionState {
  return {
    runState: 'idle',
    accounts,
    deposits,
    arInvoices,
    remittances,
    apInvoices,
  };
}
